import React, { useState } from 'react'
import {
  Button,
  Container,
  Hidden,
  Paper,
  TablePagination,
  Tooltip,
  Grid,
  Box,
} from '@material-ui/core'
import { HelpCircle as HelpCircleIcon } from 'react-feather'

import {
  ContentHeader,
  ServiceUnreachableAlert,
  DriveTour,
  LoadingFeedback,
  Page,
  FilterButton,
  StatsCard,
} from 'components'
import { ConsentsTable, ConsentsFilters } from './components'

import usePagination from 'hooks/usePagination'
import useFilter from 'hooks/useFilter'

import constants from 'constants/index'
import * as service from 'service'
import helpers from 'helpers'

import useStyles from './styles'
import useCookies from 'hooks/useCookies'
import useBrowserReachability from 'hooks/useBrowserReachability'

const ConsentList = () => {
  const { mixpanel } = helpers
  const {
    COOKIES_CONSENT_DRIVER_STEPS_OBJECT,
    COOKIES_CONSENT_MAIN_DRIVER_STEP,
    COOKIES_CONSENT_FILTERS_DRIVER_STEP,
    COOKIES_CONSENT_CARDS_DRIVER_STEP,
    COOKIES_CONSENT_TABLE_DRIVER_STEP,
  } = constants.cookies.consent

  const classes = useStyles()
  const filter = useFilter()
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const [openTour, setOpenTour] = useState(false)

  const cookiesService = useBrowserReachability(
    constants.externalServices.COOKIES_KEY,
  )

  const { response, isLoading } = useCookies(
    service.cookies.consent.get,
    {
      page,
      perPage,
      ...filter.filters,
    },
    [page, perPage, filter.filters],
  )

  const { response: statsResponse, isLoading: isStatsLoading } = useCookies(
    service.cookies.consent.getStats,
    { ...filter.filters },
    [filter.filters],
  )

  const handleOpenTour = () => {
    setOpenTour(true)
    mixpanel.track('Cookies-Consentimentos', 'Tour', {
      action: 'tour-consentimentos-cookies',
    })
  }

  const handleCardClick = (clickedKind) => {
    if (clickedKind === null) {
      const { kind, ...restFilters } = filter.filters
      filter.setFilters(restFilters)
      return
    }

    helpers.baseStatusCard.handleStatusCard({
      filter,
      key: 'kind',
      value: clickedKind,
    })
  }

  const consents = response?.data?.consents
  const totalConsents = response?.data?.meta?.totalCount || 0

  const statsData = statsResponse?.data || {}
  const statsCards = helpers.cookies.consent.formatStatsCards(statsData)

  return (
    <Page title="Cookies - Consentimentos">
      <LoadingFeedback
        open={(isLoading || isStatsLoading) && cookiesService.isReachable}
      />
      <Container maxWidth={false} className={classes.container}>
        <ContentHeader
          title="Consentimentos registrados (Logs)"
          historyBreadcumbs={false}
        >
          <Tooltip title="Iniciar o tour guiado">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
              id={COOKIES_CONSENT_MAIN_DRIVER_STEP}
            >
              Tutorial
            </Button>
          </Tooltip>
          <FilterButton
            setDrawerOpen={filter.setDrawerOpen}
            id={COOKIES_CONSENT_FILTERS_DRIVER_STEP}
          />
        </ContentHeader>

        <ServiceUnreachableAlert
          services={[constants.externalServices.COOKIES_KEY]}
        />

        <Box mb={3} id={COOKIES_CONSENT_CARDS_DRIVER_STEP}>
          <Grid container spacing={3}>
            {statsCards.map((card, index) => (
              <Grid key={index} item xs={12} sm={6} lg={3}>
                <StatsCard
                  icon={card.icon}
                  label={card.label}
                  value={card.value}
                  backgroundColor={card.backgroundColor}
                  onClick={() => handleCardClick(card.kind)}
                  selected={card.kind === (filter.filters?.kind ?? null)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper variant="outlined" id={COOKIES_CONSENT_TABLE_DRIVER_STEP}>
          <ConsentsTable consents={consents} loading={isLoading} />
          <TablePagination
            component="div"
            count={totalConsents || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={page - 1}
            rowsPerPage={perPage}
            rowsPerPageOptions={[5, 10, 25, 100]}
            labelRowsPerPage={<Hidden smDown>Por página</Hidden>}
            nextIconButtonProps={{ size: 'small' }}
            backIconButtonProps={{ size: 'small' }}
          />
        </Paper>
      </Container>
      <DriveTour
        stepsMatrix={COOKIES_CONSENT_DRIVER_STEPS_OBJECT}
        open={openTour}
        setOpen={setOpenTour}
      />
      <ConsentsFilters filter={filter} />
    </Page>
  )
}

export default ConsentList
